/**
 * Purview CVC AI Companion Section
 * Displays a card that opens the AI Assistant panel for asking questions about CVC data
 */

import { DirectionalHint, Icon, IconButton, Panel, PanelType, Spinner, SpinnerSize, TextField, TooltipHost } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import React, { useCallback, useRef, useState } from "react";
import { useAskPurviewCVCAIAssistantMutation, useGetPurviewCVCCustomersQuery } from "../../slices/PurviewCVC.slice";
import { IPurviewCVCAICompanionSectionProps, IPurviewCVCAIChatMessage } from "./PurviewCVC.interfaces";
import { CVC_AI_QUICK_PROMPTS } from "./PurviewCVC.constants";
import ReactMarkdown from "react-markdown";

// Panel styles matching BWC
const PANEL_STYLES = {
    container: {
        display: "flex",
        flexDirection: "column" as const,
        height: "100%",
        background: "#f5f5f5"
    },
    header: {
        padding: "16px 20px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        gap: "12px"
    },
    headerIcon: {
        width: "40px",
        height: "40px",
        borderRadius: "10px",
        background: "rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px"
    },
    headerTitle: {
        fontSize: "18px",
        fontWeight: 600
    },
    headerSubtitle: {
        fontSize: "12px",
        opacity: 0.9
    },
    dataSummary: {
        padding: "12px 16px",
        background: "#e8f5e9",
        borderBottom: "1px solid #c8e6c9",
        fontSize: "12px",
        color: "#2e7d32",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    },
    infoBanner: {
        padding: "10px 16px",
        background: "#fff8e1",
        borderBottom: "1px solid #ffe082",
        fontSize: "12px",
        color: "#8d6e00",
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        lineHeight: 1.45
    },
    infoBannerIcon: {
        fontSize: "14px",
        color: "#f5a623",
        marginTop: "1px",
        flexShrink: 0
    },
    scenariosContainer: {
        padding: "16px",
        background: "#fff",
        borderBottom: "1px solid #e1e1e1"
    },
    scenariosTitle: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#333",
        marginBottom: "12px",
        display: "flex",
        alignItems: "center",
        gap: "6px"
    },
    scenariosGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "10px"
    },
    scenarioCard: {
        padding: "12px",
        background: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        cursor: "pointer",
        transition: "all 0.2s ease"
    },
    scenarioIcon: {
        width: "28px",
        height: "28px",
        borderRadius: "6px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "14px",
        marginBottom: "8px"
    },
    scenarioTitle: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#1a1a2e",
        marginBottom: "4px"
    },
    scenarioDesc: {
        fontSize: "11px",
        color: "#64748b",
        lineHeight: 1.4
    },
    chatContainer: {
        flex: 1,
        overflowY: "auto" as const,
        padding: "16px",
        display: "flex",
        flexDirection: "column" as const,
        gap: "12px"
    },
    messageUser: {
        alignSelf: "flex-end" as const,
        maxWidth: "80%",
        padding: "12px 16px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        borderRadius: "16px 16px 4px 16px",
        fontSize: "14px",
        lineHeight: 1.5
    },
    messageAssistant: {
        alignSelf: "flex-start" as const,
        maxWidth: "85%",
        padding: "14px 18px",
        background: "#fff",
        borderRadius: "16px 16px 16px 4px",
        fontSize: "14px",
        lineHeight: 1.6,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #e2e8f0"
    },
    messageLoading: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        color: "#64748b"
    },
    inputContainer: {
        padding: "16px",
        background: "#fff",
        borderTop: "1px solid #e1e1e1",
        display: "flex",
        gap: "12px",
        alignItems: "flex-end"
    },
    emptyState: {
        flex: 1,
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        textAlign: "center" as const,
        color: "#64748b"
    }
};

// Markdown styles
const markdownStyles = `
    .cvc-ai-markdown h1,
    .cvc-ai-markdown h2,
    .cvc-ai-markdown h3,
    .cvc-ai-markdown h4 {
        margin-top: 16px;
        margin-bottom: 8px;
        font-weight: 600;
        color: #1a1a2e;
    }
    .cvc-ai-markdown h3 {
        font-size: 15px;
        color: #4338ca;
        border-bottom: 2px solid #e0e7ff;
        padding-bottom: 6px;
        margin-top: 20px;
    }
    .cvc-ai-markdown ol,
    .cvc-ai-markdown ul {
        padding-left: 20px;
        margin: 8px 0;
    }
    .cvc-ai-markdown li {
        margin-bottom: 10px;
        line-height: 1.6;
        padding-left: 4px;
    }
    .cvc-ai-markdown strong {
        font-weight: 600;
        color: #1e40af;
        background: linear-gradient(120deg, #e0e7ff 0%, #c7d2fe 100%);
        padding: 1px 6px;
        border-radius: 4px;
    }
    .cvc-ai-markdown p {
        margin: 8px 0;
        line-height: 1.6;
    }
`;

// AI Prompt Scenarios for CVC
const AI_PROMPT_SCENARIOS = [
    {
        id: "weekly-cvc-insights",
        icon: "Calendar",
        title: "This week's Purview CVC Customer Insights",
        description: "Weekly summary focusing on recent activity and changes",
        prompt: `This Week's Purview CVC Activity Summary

Analyze the Purview CVC customer engagement data and provide:
1. **Weekly Highlights**: Key activities and updates from the past 7 days
2. **Top Engagements**: Most active customer-solution pairs
3. **CVC Lead Performance**: Summary by CVC Lead activity
4. **Solution Distribution**: Which solutions are most engaged
5. **Attention Items**: Any engagements that need follow-up

Focus on actionable insights for the team.`
    },
    {
        id: "engagement-hygiene",
        icon: "Health",
        title: "Engagements Hygiene",
        description: "Identify engagements with missing notes or stale data",
        prompt: `Engagement Hygiene Check

Review the Purview CVC data for:
1. **Stale Engagements**: Customers with no recent notes (30+ days)
2. **Missing Data**: Engagements with incomplete information
3. **High Priority Follow-ups**: Engagements that need immediate attention
4. **PM Coverage Gaps**: Customers without assigned PMs
5. **Recommendations**: Actions to improve engagement hygiene

Provide a clear action list for the team.`
    },
    {
        id: "solution-analysis",
        icon: "BarChartVertical",
        title: "Solution Analysis",
        description: "Breakdown of customer engagement by solution",
        prompt: `Analyze customer engagement by solution:
1. Which solutions have the most active engagements?
2. Which solutions are growing vs declining?
3. Top CVC Leads per solution
4. Customer distribution across solutions
5. Any solutions needing more attention?`
    },
    {
        id: "cvc-lead-overview",
        icon: "Org",
        title: "CVC Lead Overview",
        description: "Overview of CVC Leads and their customer portfolios",
        prompt: `Provide an overview of CVC Lead performance:
1. Customer count per CVC Lead
2. Solution diversity per CVC Lead
3. Recent activity levels
4. Any workload imbalances?
5. Top performing CVC Leads`
    }
];

/**
 * Scenario Card Component
 */
const ScenarioCard: React.FC<{
    scenario: typeof AI_PROMPT_SCENARIOS[0];
    onClick: () => void;
}> = ({ scenario, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            style={{
                ...PANEL_STYLES.scenarioCard,
                ...(isHovered ? { background: "#e8f4fd", borderColor: "#667eea", boxShadow: "0 2px 8px rgba(102, 126, 234, 0.2)" } : {})
            }}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div style={PANEL_STYLES.scenarioIcon}>
                <Icon iconName={scenario.icon} />
            </div>
            <div style={PANEL_STYLES.scenarioTitle}>{scenario.title}</div>
            <div style={PANEL_STYLES.scenarioDesc}>{scenario.description}</div>
        </div>
    );
};

/**
 * AI Assistant Panel Component - Matching BWC style
 */
const PurviewCVCAIAssistantPanel: React.FC<{
    isOpen: boolean;
    onDismiss: () => void;
    initialPrompt?: string;
    messages: IPurviewCVCAIChatMessage[];
    setMessages: React.Dispatch<React.SetStateAction<IPurviewCVCAIChatMessage[]>>;
    sessionId: string;
    setSessionId: (id: string) => void;
    totalCustomers: number;
    totalEngagements: number;
    totalSolutions: number;
}> = ({ isOpen, onDismiss, initialPrompt, messages, setMessages, sessionId, setSessionId, totalCustomers, totalEngagements, totalSolutions }) => {
    const [inputValue, setInputValue] = useState("");
    const [askAI, { isLoading }] = useAskPurviewCVCAIAssistantMutation();
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const hasProcessedInitialPrompt = useRef(false);

    // Process initial prompt
    React.useEffect(() => {
        if (isOpen && initialPrompt && !hasProcessedInitialPrompt.current) {
            hasProcessedInitialPrompt.current = true;
            handleSendMessage(initialPrompt);
        }
    }, [isOpen, initialPrompt]);

    // Reset processed flag when panel closes
    React.useEffect(() => {
        if (!isOpen) {
            hasProcessedInitialPrompt.current = false;
        }
    }, [isOpen]);

    // Scroll to bottom when new messages arrive
    React.useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: "smooth"
            });
        }
    }, [messages]);

    const handleSendMessage = async (question: string) => {
        if (!question.trim() || isLoading) return;

        const userMessage: IPurviewCVCAIChatMessage = { role: "user", content: question };
        setMessages(prev => [...prev, userMessage]);
        setInputValue("");

        try {
            const response = await askAI({
                question,
                sessionId,
                conversationHistory: messages
            }).unwrap();

            const assistantMessage: IPurviewCVCAIChatMessage = {
                role: "assistant",
                content: response.response
            };
            setMessages(prev => [...prev, assistantMessage]);
        } catch {
            const errorMessage: IPurviewCVCAIChatMessage = {
                role: "assistant",
                content: "I apologize, but I encountered an error processing your request. Please try again."
            };
            setMessages(prev => [...prev, errorMessage]);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(inputValue);
        }
    };

    const handleClearConversation = () => {
        setMessages([]);
        setSessionId(`cvc-ai-${Date.now()}`);
        hasProcessedInitialPrompt.current = false;
    };

    const handleScenarioClick = (prompt: string) => {
        handleSendMessage(prompt);
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText=""
            isLightDismiss
            styles={{
                main: { maxWidth: "600px" },
                content: { padding: 0 },
                scrollableContent: { height: "100%" },
                commands: { display: "none" }
            }}
        >
            <div style={PANEL_STYLES.container}>
                {/* Header - Matching BWC style */}
                <div style={PANEL_STYLES.header}>
                    <div style={PANEL_STYLES.headerIcon}>
                        <Icon iconName="Robot" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={PANEL_STYLES.headerTitle}>Purview CVC AI Assistant</div>
                        <div style={PANEL_STYLES.headerSubtitle}>
                            Ask questions about your Purview CVC data
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "4px" }}>
                        {messages.length > 0 && (
                            <TooltipHost content="Clear conversation">
                                <IconButton
                                    iconProps={{ iconName: "Delete" }}
                                    onClick={handleClearConversation}
                                    disabled={isLoading}
                                    styles={{
                                        root: { color: "#fff" },
                                        rootHovered: { color: "#fff", background: "rgba(255,255,255,0.1)" },
                                        rootDisabled: { color: "rgba(255,255,255,0.5)" }
                                    }}
                                />
                            </TooltipHost>
                        )}
                        <IconButton
                            iconProps={{ iconName: "Cancel" }}
                            onClick={onDismiss}
                            styles={{
                                root: { color: "#fff" },
                                rootHovered: { color: "#fff", background: "rgba(255,255,255,0.1)" }
                            }}
                        />
                    </div>
                </div>

                {/* Data Summary Bar */}
                {totalEngagements > 0 && (
                    <div style={PANEL_STYLES.dataSummary}>
                        <Icon iconName="Database" />
                        <span>
                            Loaded {totalCustomers} unique customers, {totalEngagements} engagements across {totalSolutions} solutions
                        </span>
                    </div>
                )}

                {/* Always-visible notice: how the assistant answers */}
                <div style={PANEL_STYLES.infoBanner}>
                    <Icon iconName="Info" style={PANEL_STYLES.infoBannerIcon} />
                    <span>
                        <strong>How answers work:</strong> Totals, counts and &ldquo;top / best&rdquo; insights are
                        calculated from the <strong>entire dataset</strong>. For detailed lookups the assistant reads
                        the <strong>most relevant records ranked by your question</strong> (not every row), so very
                        broad &ldquo;list everything&rdquo; requests may show a representative subset. Ask about a
                        specific customer, solution or owner for the most precise results.
                    </span>
                </div>

                {/* Prompt Scenarios (show when no messages) */}
                {messages.length === 0 && (
                    <div style={PANEL_STYLES.scenariosContainer}>
                        <div style={PANEL_STYLES.scenariosTitle}>
                            <Icon iconName="Lightbulb" style={{ color: "#667eea" }} />
                            Quick Insights - Click to explore
                        </div>
                        <div style={PANEL_STYLES.scenariosGrid}>
                            {AI_PROMPT_SCENARIOS.map(scenario => (
                                <ScenarioCard
                                    key={scenario.id}
                                    scenario={scenario}
                                    onClick={() => handleScenarioClick(scenario.prompt)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Chat Messages */}
                <div ref={chatContainerRef} style={PANEL_STYLES.chatContainer}>
                    <style>{markdownStyles}</style>
                    {messages.length === 0 ? (
                        <div style={PANEL_STYLES.emptyState}>
                            <Icon iconName="ChatBot" style={{ fontSize: 48, opacity: 0.5, marginBottom: 16 }} />
                            <div style={{ fontSize: "16px", fontWeight: 500, marginBottom: "8px" }}>
                                Start a conversation
                            </div>
                            <div style={{ fontSize: "13px" }}>
                                Choose a quick insight above or type your own question below
                            </div>
                        </div>
                    ) : (
                        messages.map((msg, index) => (
                            <div
                                key={index}
                                style={msg.role === "user" ? PANEL_STYLES.messageUser : PANEL_STYLES.messageAssistant}
                            >
                                {msg.role === "assistant" ? (
                                    <div className="cvc-ai-markdown">
                                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                                    </div>
                                ) : (
                                    msg.content
                                )}
                            </div>
                        ))
                    )}

                    {isLoading && (
                        <div style={{ ...PANEL_STYLES.messageAssistant, width: "fit-content" }}>
                            <div style={PANEL_STYLES.messageLoading}>
                                <Spinner size={SpinnerSize.small} />
                                <span>Analyzing data...</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Input Area */}
                <div style={PANEL_STYLES.inputContainer}>
                    <TextField
                        placeholder="Ask a question about Purview CVC data..."
                        value={inputValue}
                        onChange={(_, newValue) => setInputValue(newValue || "")}
                        onKeyPress={handleKeyPress}
                        multiline
                        autoAdjustHeight
                        disabled={isLoading}
                        styles={{
                            root: { flex: 1 },
                            fieldGroup: {
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0"
                            }
                        }}
                    />
                    <TooltipHost content="Send message" directionalHint={DirectionalHint.topCenter}>
                        <IconButton
                            iconProps={{ iconName: "Send" }}
                            onClick={() => handleSendMessage(inputValue)}
                            disabled={!inputValue.trim() || isLoading}
                            styles={{
                                root: {
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "8px",
                                    background: inputValue.trim() ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "#e2e8f0",
                                    color: inputValue.trim() ? "#fff" : "#94a3b8"
                                },
                                rootHovered: {
                                    background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                                    color: "#fff"
                                },
                                rootDisabled: {
                                    background: "#e2e8f0",
                                    color: "#94a3b8"
                                }
                            }}
                        />
                    </TooltipHost>
                </div>
            </div>
        </Panel>
    );
};

/**
 * Quick Insight Button Component - Matching BWC style
 */
const QuickInsightButton: React.FC<{
    icon: string;
    label: string;
    onClick: () => void;
}> = ({ icon, label, onClick }) => (
    <button
        onClick={onClick}
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 14px",
            background: "rgba(255, 255, 255, 0.15)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            color: "#ffffff",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.2s ease"
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.25)";
            e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
            e.currentTarget.style.transform = "translateY(0)";
        }}
    >
        <span>{icon}</span>
        <span>{label}</span>
    </button>
);

/**
 * Main AI Companion Section Component
 */
const PurviewCVCAICompanionSection: React.FC<IPurviewCVCAICompanionSectionProps> = ({
    totalCustomers,
    totalEngagements,
    totalSolutions
}) => {
    const [isPanelOpen, { setTrue: openPanel, setFalse: closePanel }] = useBoolean(false);
    const [initialPrompt, setInitialPrompt] = useState<string | undefined>();
    const [messages, setMessages] = useState<IPurviewCVCAIChatMessage[]>([]);
    const [sessionId, setSessionId] = useState<string>(`cvc-ai-${Date.now()}`);

    // Quick prompts
    const quickPrompts = [
        {
            id: "weekly-cvc-insights",
            icon: "📊",
            label: "This week's Purview CVC Customer Insights",
            prompt: `This Week's Purview CVC Activity Summary

Analyze the Purview CVC customer engagement data and provide:
1. **Weekly Highlights**: Key activities and updates from the past 7 days
2. **Top Engagements**: Most active customer-solution pairs
3. **CVC Lead Performance**: Summary by CVC Lead activity
4. **Solution Distribution**: Which solutions are most engaged
5. **Attention Items**: Any engagements that need follow-up

Focus on actionable insights for the team.`
        },
        {
            id: "engagement-hygiene",
            icon: "🔍",
            label: "Engagements Hygiene",
            prompt: `Engagement Hygiene Check

Review the Purview CVC data for:
1. **Stale Engagements**: Customers with no recent notes (30+ days)
2. **Missing Data**: Engagements with incomplete information
3. **High Priority Follow-ups**: Engagements that need immediate attention
4. **PM Coverage Gaps**: Customers without assigned PMs
5. **Recommendations**: Actions to improve engagement hygiene

Provide a clear action list for the team.`
        }
    ];

    const handleQuickPromptClick = (prompt: string) => {
        setInitialPrompt(prompt);
        setMessages([]);
        openPanel();
    };

    const handleAskAssistant = () => {
        setInitialPrompt(undefined);
        openPanel();
    };

    return (
        <>
            <div style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "16px",
                padding: "20px 24px",
                marginBottom: "20px",
                boxShadow: "0 8px 32px rgba(102, 126, 234, 0.35)",
                position: "relative",
                overflow: "hidden",
                color: "#ffffff"
            }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
                        <div style={{
                            width: 56,
                            height: 56,
                            borderRadius: "14px",
                            background: "rgba(255,255,255,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 26,
                            flexShrink: 0
                        }}>
                            <Icon iconName="ChatBot" />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 18, display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                AI Companion
                                <span style={{
                                    fontSize: 11,
                                    padding: "3px 8px",
                                    background: "rgba(255,255,255,0.2)",
                                    borderRadius: 12
                                }}>
                                    ✨
                                </span>
                            </div>
                            <div style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.4 }}>
                                Ask questions about your Purview Customer data. Get insights on engagements, and more.
                            </div>
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: 12, opacity: 0.9 }}>
                            <span>Quick Insights</span>
                        </div>
                        {quickPrompts.map((prompt) => (
                            <QuickInsightButton
                                key={prompt.id}
                                icon={prompt.icon}
                                label={prompt.label}
                                onClick={() => handleQuickPromptClick(prompt.prompt)}
                            />
                        ))}
                    </div>

                    <button
                        onClick={handleAskAssistant}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "14px 24px",
                            background: "rgba(255, 255, 255, 0.95)",
                            color: "#667eea",
                            borderRadius: "12px",
                            fontSize: "14px",
                            fontWeight: 600,
                            cursor: "pointer",
                            border: "none",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                            transition: "all 0.2s ease",
                            flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.15)";
                        }}
                    >
                        <Icon iconName="ChatBot" style={{ fontSize: 16 }} />
                        <span>Ask AI Assistant</span>
                    </button>
                </div>
            </div>

            <PurviewCVCAIAssistantPanel
                isOpen={isPanelOpen}
                onDismiss={closePanel}
                initialPrompt={initialPrompt}
                messages={messages}
                setMessages={setMessages}
                sessionId={sessionId}
                setSessionId={setSessionId}
                totalCustomers={totalCustomers}
                totalEngagements={totalEngagements}
                totalSolutions={totalSolutions}
            />
        </>
    );
};

export default PurviewCVCAICompanionSection;
